import { FileExcelOutlined, FileImageOutlined, FileOutlined, FilePdfOutlined, FileWordOutlined, FolderOutlined, SearchOutlined } from '@ant-design/icons';
import { Alert, Breadcrumb, Button, Empty, Input, Segmented, Space, Table, Tag, Tree, Typography, theme } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { useMemo, useState } from 'react';
import type { CSSProperties, Key } from 'react';
import type { DownloadTask, DownloadTaskItem } from '@/services/downloadTasks';
import { buildDownloadFileTree, downloadFileSize, downloadStatusColor, statusLabels } from './downloadFileTree';
import type { DownloadDirectory, DownloadFileRow } from './downloadFileTree';
import styles from './DownloadFileDetails.less';

/** 文件明细展示边界；原生ZIP下载仍由下载中心统一管理。 */
interface DownloadFileDetailsProps {
  /** 所属下载任务和全局统计，不是业务报告行。 */
  task: DownloadTask;
  /** 已完整加载的冻结明细，包含失败及没有文件名的记录。 */
  items: DownloadTaskItem[];
  /** 服务端文件类型名称，兼容历史协议编码。 */
  labels: Record<string, string>;
  /** 正在读取接口；未完成全量加载时不展示局部目录。 */
  loading: boolean;
  /** 请求失败信息，不含底层响应、凭据或存储路径。 */
  error: string;
  /** 用户主动重试失败的明细查询，不重新打包。 */
  onReload: () => void;
}

/** 文件结果筛选；READY和PENDING合并为“处理中”，不改写后端状态。 */
type ResultFilter = 'all' | 'success' | 'failed' | 'pending';

/** @param row 文件行 @returns 沿用Ant Design文件图标，类型来源不改变文件下载行为 */
function FileIcon({ row }: { row: DownloadFileRow }) {
  const extension = row.name.split('.').pop()?.toLowerCase();
  if (extension === 'pdf') return <FilePdfOutlined />;
  if (['doc', 'docx'].includes(extension || '')) return <FileWordOutlined />;
  if (['xls', 'xlsx'].includes(extension || '')) return <FileExcelOutlined />;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(extension || '')) return <FileImageOutlined />;
  return <FileOutlined />;
}

/** @param status 明细后台状态 @param filter 当前筛选 @returns 是否符合选中的结果集合 */
function matchesStatus(status: DownloadTaskItem['status'], filter: ResultFilter): boolean {
  return filter === 'all' || (filter === 'success' ? status === 'SUCCESS' : filter === 'failed' ? status === 'FAILED'
    : status === 'PENDING' || status === 'READY');
}

/**
 * 完整目录导航与文件列表，继承业务主题颜色、字体和控件，长报告名仅在任务上下文显示。
 * @param props 已鉴权的任务、完整文件清单、类型名称及查询状态；不接收下载地址
 * @returns 仅供查看和筛选的目录清单，不触发生成、打包或文件下载
 */
export default function DownloadFileDetails({ task, items, labels, loading, error, onReload }: DownloadFileDetailsProps) {
  const { token } = theme.useToken();
  const [directory, setDirectory] = useState('all');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<ResultFilter>('all');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Key[] | undefined>();
  const model = useMemo(() => buildDownloadFileTree(items, labels), [items, labels]);
  const selected = model.nodes.get(directory) || model.root;
  const scope = model.rows.filter(row => row.ancestorKeys.includes(selected.key));
  const needle = query.trim().toLocaleLowerCase();
  const searched = scope.filter(row => !needle || row.searchText.includes(needle));
  const rows = searched.filter(row => matchesStatus(row.item.status, filter));
  const successes = searched.filter(row => row.item.status === 'SUCCESS').length;
  const failures = searched.filter(row => row.item.status === 'FAILED').length;
  const pending = searched.length - successes - failures;
  const taskCount = model.root.children.length;
  const currentTask = selected.taskId ? model.root.children.find(node => node.taskId === selected.taskId)
    : taskCount === 1 ? model.root.children[0] : undefined;

  /** @param node 目录及后代统计 @returns 仅展示单个目录段的树节点；悬停保留完整名称和失败数量 */
  function treeNode(node: DownloadDirectory): DataNode {
    return { key: node.key, icon: <FolderOutlined />, title: <span className={styles.directoryTitle}>
      <span className={styles.directoryName} title={node.taskId && !node.path.length
        ? `${node.name}\n任务ID：${node.taskId}` : node.name}>{node.name}</span>
      <span className={styles.directoryCount} title={`${node.count} 项，失败 ${node.failures} 项`}>{node.count}</span>
    </span>, children: node.children.map(treeNode) };
  }

  /** @param keys 树选中的目录键，只取首个；切换目录只重置本地页码，不请求ZIP或更换清单 */
  function selectDirectory(keys: Key[]): void {
    if (keys.length) { setDirectory(String(keys[0])); setPage(1); }
  }

  return <div className={styles.details} style={{
    '--download-border': token.colorBorderSecondary, '--download-muted': token.colorTextSecondary,
    '--download-primary': token.colorPrimary,
  } as CSSProperties}>
    <div className={styles.summary}>
      <Space size={8} wrap>
        <Tag color={downloadStatusColor(task.status)}>{statusLabels[task.status]}</Tag>
        <Typography.Text>{task.taskCount} 个任务</Typography.Text>
        <Typography.Text type="secondary">{task.totalCount} 项明细 · 成功 {task.successCount} · 失败 {task.failureCount}</Typography.Text>
      </Space>
      <Typography.Text type="secondary">文件目录与 ZIP 保持一致，未产出文件的类型归入“未打包项目”</Typography.Text>
    </div>
    {error && <div className={styles.error}><Alert type="error" showIcon message={error}
      action={<Button size="small" onClick={onReload}>重新加载</Button>} /></div>}
    <div className={styles.workspace}>
      <aside className={styles.sidebar} aria-label="下载文件目录">
        <div className={styles.sidebarHeading}>
          <Typography.Text strong>文件目录</Typography.Text>
          <Button type="link" size="small" onClick={() => setExpanded(expanded === undefined ? [] : undefined)}>
            {expanded === undefined ? '收起目录' : '展开目录'}
          </Button>
        </div>
        <Tree blockNode showIcon selectedKeys={[selected.key]}
          expandedKeys={expanded ?? [...model.nodes.keys()]} onExpand={keys => setExpanded(keys)}
          onSelect={selectDirectory} treeData={[treeNode(model.root)]} />
      </aside>
      <section className={styles.content} aria-label="下载文件清单">
        <div className={styles.context}>
          <Typography.Title level={5}>{currentTask?.name || '全部任务'}</Typography.Title>
          <Typography.Text type="secondary">{currentTask ? `任务ID：${currentTask.taskId}` : `共 ${taskCount} 个业务任务，按目录查看文件明细`}</Typography.Text>
          {selected.path.length > 0 && <Breadcrumb items={selected.path.map(title => ({ title }))} />}
        </div>
        <div className={styles.toolbar}>
          <Segmented value={filter} onChange={value => { setFilter(value as ResultFilter); setPage(1); }}
            options={[{ label: `全部 ${searched.length}`, value: 'all' }, { label: `成功 ${successes}`, value: 'success' },
              { label: `失败 ${failures}`, value: 'failed' }, ...(pending ? [{ label: `处理中 ${pending}`, value: 'pending' }] : [])]} />
          <Input aria-label="搜索下载文件" allowClear prefix={<SearchOutlined />} value={query}
            placeholder="搜索文件名、目录或失败原因" className={styles.search}
            onChange={event => { setQuery(event.target.value); setPage(1); }} />
        </div>
        <Table<DownloadFileRow> size="middle" rowKey={row => row.item.id} dataSource={rows} loading={loading}
          tableLayout="fixed" scroll={{ x: 660 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={loading ? '正在加载文件清单'
            : error ? '文件清单尚未加载成功' : !items.length ? '文件清单尚未生成，后台处理后会自动更新'
            : '当前目录或筛选条件下没有文件'} /> }}
          pagination={{ current: Math.min(page, Math.max(1, Math.ceil(rows.length / 20))), pageSize: 20,
            total: rows.length, showSizeChanger: false, onChange: setPage, showTotal: count => `共 ${count} 项` }}
          columns={[
            { title: '文件名称', key: 'name', render: (_, row) => <div className={styles.file}>
              <span className={styles.fileIcon}><FileIcon row={row} /></span>
              <div className={styles.fileText}>
                <Typography.Text className={styles.fileName} title={row.item.fileName || row.name}>{row.name}</Typography.Text>
                <Typography.Text type="secondary" className={styles.fileContext}>
                  {selected.key === 'all' && taskCount > 1 ? `${row.item.taskId} · ` : ''}{row.directories.join(' / ') || '任务根目录'}
                </Typography.Text>
                {row.item.errorMessage && <Typography.Text type="danger" className={styles.failure}>{row.item.errorMessage}</Typography.Text>}
              </div>
            </div> },
            { title: '文件类型', key: 'format', width: 130, render: (_, row) => labels[row.item.format] || row.item.format },
            { title: '大小', key: 'size', width: 100, render: (_, row) => Number(row.item.fileSize || 0) > 0 ? downloadFileSize(row.item.fileSize) : '—' },
            { title: '结果', key: 'status', width: 84, render: (_, row) => <Tag
              color={row.item.status === 'SUCCESS' ? 'green' : row.item.status === 'FAILED' ? 'red' : 'blue'}>
              {{ PENDING: '待解析', READY: '待打包', SUCCESS: '成功', FAILED: '失败' }[row.item.status]}</Tag> },
          ]} />
      </section>
    </div>
  </div>;
}
